import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Pricelist } from "assets/SideBarIcons/price-tag.svg";
import StyledInput from "common/SearchField/Search";
import { useAssignPriceListServiceService, useGetPriceListServiceService, useUnassignPriceListServiceService, useUpdatePriceListServiceService } from 'subModule/src/services/pricelist/usePriceList';
import PricelistTabs from '../PricelistTabs';
import { Grid } from '@mui/material';
import { PricelistServicesTable } from './PricelistServicesTable';
import Loader from 'layout/Loader';
import { useForm } from 'react-hook-form';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import PriceListActions from 'components/PriceList/PriceListActions';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';
import { PriceListProfileService } from 'subModule/src/core/services';

export default function PricelistServicesListing() {
    const params = useParams();
    const pricelistId = params?.id;
    const navigate = useNavigate();
    const [pricelistTitle, setPricelistTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [search, setSearch] = useState<any>('');
    const [searchTerm, setSearchTerm] = useState<any>('');
    const { register, control, formState: { errors }, watch, setValue, trigger, reset } = useForm<FormData>({});
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetPriceListServiceService({ pageNumber: pageNo, size: pageSize, title: searchTerm }, +(params as any).id) : useGetPriceListServiceService({ pageNumber: pageNo, size: pageSize }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!pricelistId) return;
        let active = true;
        PriceListProfileService.getPricelistById(+pricelistId)
            .then((res: unknown) => {
                if (!active) return;
                const name = pickProfileEntityTitle(res);
                if (name) setPricelistTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [pricelistId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'priceLists', href: '/price-lists' },
            {
                labelKey: 'title',
                ...(pricelistTitle ? { label: pricelistTitle } : {}),
                href: pricelistId ? `/price-list/${pricelistId}` : undefined,
            },
            { labelKey: 'assignService' },
        ],
        [pricelistId, pricelistTitle],
    );

    const columns = [
        { id: 'serviceId', label: '', numeric: false, disablePadding: false },
        { id: 'title', label: t('name'), numeric: false },
        { id: 'defaultPrice', label: t('defaultPrice'), numeric: false },
        { id: 'price', label: t('price'), numeric: false },
    ];
    const fetchData = useCallback(() => {
        reset();
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, [refetch, reset]);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, searchTerm, fetchData]);
    const handleChangePage = (event: unknown, newPage: number) => {
        setPageNo(newPage + 1);
        setPageSize(pageSize);
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(1);
    };
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') handleSearch(trimmedSearch);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onUnassignService({ pricelistId: +(params as any).id, serviceId: row.id });
        } else {
            onAssignService({ pricelistId: +(params as any).id, serviceId: row.id, price: row.price });
        }
    }
    const handleBlurItem = (row: any) => {
        onUpdatePriceListService({ pricelistId: +(params as any).id, serviceId: row.id, price: row.price });
    }
    const { onAssignService } = useAssignPriceListServiceService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('service') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={response?.message || 'Something went wrong'} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignService } = useUnassignPriceListServiceService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('service') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={response?.message || 'Something went wrong'} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onUpdatePriceListService } = useUpdatePriceListServiceService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                refetch();
                reset();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('priceList') + ' ' + t('update') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={response?.message || 'Something went wrong'} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/price-lists')}
                heading={t('priceLists')}
                icon={<Pricelist height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <PricelistTabs params={params} selectedIndex={2} /> : null}
                    <Box className='w-[100%]'>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={7} lg={5} className='mt-3'>
                                <StyledInput
                                    fullWidth
                                    placeholder={t("name")}
                                    handleSearchClick={handleSearchClick}
                                    handleChange={handleInputChange}
                                    value={search}
                                    clearSearch={clearSearch}
                                />
                            </Grid>
                            <Grid item xs={12} md={5} lg={7} className='mt-3'>
                                <Box display="flex" justifyContent="flex-end">
                                    <PriceListActions
                                        type="service"
                                        pricelistId={+(params as any).id}
                                        onUploadSuccess={fetchData}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        {(isFetching || loading) ? <Loader /> : <PricelistServicesTable
                            columns={columns}
                            rows={tableData?.pages[0]?.data?.content || []}
                            tableData={tableData?.pages[0]?.data || {}}
                            assignUnassignSingleEntity={assignUnassignSingleEntity}
                            form={{ register, control, formState: { errors }, watch, setValue, trigger, reset }}
                            assignPrivilege={checkPrivileges('ROLE_SERVICE_PRICELIST_ASSIGN')}
                            unassignPrivilege={checkPrivileges('ROLE_SERVICE_PRICELIST_UNASSIGN')}
                            handleBlurItem={handleBlurItem}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            {...{pageNo, pageSize}}
                        />}
                    </Box>
                </div>
            </Box>
        </Box>
    )
}
